<script setup>
/* global defineProps, defineEmits */
import { computed } from 'vue';
import { statusLabels } from '@/views/team-holidays/teamHolidayStatus';
const props = defineProps({
  modelValue: { type: Boolean, default: false },
  employeeName: { type: String, default: '' },
  period: { type: String, default: '' },
  statusLabel: { type: String, default: '' },
  status: { type: String, default: '' },
  loading: { type: Boolean, default: false },
});
const displayedStatus = computed(() => Object.hasOwn(statusLabels, props.status) ? statusLabels[props.status] : props.statusLabel || 'Sin estado');
const emit = defineEmits(['update:modelValue', 'confirm']);
const cancel = () => {
  if (!props.loading) emit('update:modelValue', false);
};
</script>

<template>
  <el-dialog :model-value="modelValue" width="600px" class="holiday-status-confirm-dialog"
    :show-close="false" :lock-scroll="true" :close-on-click-modal="!loading"
    :close-on-press-escape="!loading" align-center @update:model-value="cancel">
    <template #header="{ titleId, titleClass }">
      <div class="holiday-status-confirm__header">
        <h2 :id="titleId" :class="titleClass" class="holiday-status-confirm__title">
          <svg class="holiday-status-confirm__asterisk" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="724" height="1024" viewBox="0 0 724 1024">
          <path
            d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
        </svg>
          Cambiar estado de vacaciones
        </h2>
        <button type="button" class="holiday-status-confirm__close" aria-label="Cerrar" :disabled="loading" @click="cancel">
          <img src="@/assets/images/aspa_blue_small_entrada.png" alt="">
        </button>
      </div>
    </template>
    <dl class="holiday-status-confirm__summary">
      <div><dt>Empleado</dt><dd>{{ employeeName }}</dd></div>
      <div><dt>Periodo</dt><dd>{{ period }}</dd></div>
      <div><dt>Nuevo estado</dt><dd class="holiday-status-confirm__status" :class="`holiday-status--${status}`">{{ displayedStatus }}</dd></div>
    </dl>
    <template #footer>
      <div class="holiday-status-confirm__actions">
        <button type="button" class="btn btn_yellow px-4" :disabled="loading" @click="cancel">CANCELAR</button>
        <button type="button" class="btn btn_red px-4" :disabled="loading" @click="!loading && emit('confirm')">CONFIRMAR</button>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss">
/* Element Plus teleports the dialog; keep these overrides local to its unique class. */
.el-dialog.holiday-status-confirm-dialog {
  --el-dialog-bg-color: #f3f0fb;
  --el-dialog-padding-primary: 24px;
  max-width: calc(100vw - 24px);
  max-height: calc(100dvh - 24px);
  margin: auto;
  border-radius: 20px;
  overflow-y: auto;

  .el-dialog__header { margin-right: 0; padding: 0 0 24px; }
  .el-dialog__body { padding: 0; min-height: auto !important; overflow: visible; }
  .el-dialog__footer { padding: 0; }

  @media (max-width: 767px) {
    --el-dialog-width: calc(100vw - 24px) !important;
    --el-dialog-padding-primary: 20px;
    .el-dialog__header { padding-bottom: 24px; }
  }
}
</style>

<style lang="scss" scoped>
.holiday-status-confirm__header { position: relative; display: flex; align-items: flex-start; gap: 20px; font-size: 14px; }
.holiday-status-confirm__title {
  flex: 1;
  min-width: 0;
  margin: 16px 0 0;
  color: #f9156f;
  font-size: 3.5em;
  font-weight: 900;
  letter-spacing: -0.05em;
  line-height: 1.2;
  overflow-wrap: anywhere;
}
.holiday-status-confirm__asterisk { position: absolute; top: 0; left: 0; width: 2.2em; height: auto; fill: #f9156f; font-size: 14px; }
.holiday-status-confirm__close {
  flex: 0 0 8%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 8%;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 4px;
  img { width: 100%; height: auto; object-fit: contain; }
}
.holiday-status-confirm__summary {
  display: grid;
  gap: 16px;
  margin: 0;
  padding-bottom: 24px;
  > div { display: grid; grid-template-columns: 140px minmax(0, 1fr); gap: 20px; align-items: baseline; }
  dt { color: #68677e; font-size: 16px; font-weight: 400; }
  dd { min-width: 0; margin: 0; color: var(--holiday-status-color, #202062); font-size: 16px; line-height: 1.4; overflow-wrap: anywhere; }
  .holiday-status-confirm__status {
    font-weight: 700;
    text-transform: none;
  }
}
.holiday-status-confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #ddd7f2;
  .btn { flex: 0 1 25%; width: 25%; margin: 0; border-radius: 20px; }
  .btn_yellow { color: #474ce6; }
  .btn_red { color: #fff; }
}
button:focus-visible { outline: 2px solid #409eff; outline-offset: 3px; }
button:disabled { cursor: not-allowed; opacity: .5; }
@media (max-width: 767px) {
  .holiday-status-confirm__header { gap: 12px; }
  .holiday-status-confirm__title { margin-top: 0; font-size: 2em; }
  .holiday-status-confirm__asterisk { display: none; }
  .holiday-status-confirm__summary {
    gap: 20px;
    padding-bottom: 24px;
    > div { grid-template-columns: minmax(0, 1fr); gap: 4px; }
    dt { font-size: 16px; }
    dd { font-size: 16px; }
  }
  .holiday-status-confirm__actions {
    flex-direction: column; gap: 12px; padding-top: 20px;
    .btn { flex: 0 0 auto; width: 100%; min-height: 44px; }
  }
}
</style>
